import type { Pagination } from '@aigc/types';
import { hashKey } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

const INIT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = 100;

export interface QueryHelperHookOptions<TQuery> {
  defaultQueryParams?: TQuery;
  defaultPagination?: Partial<Pagination>;
  defaultEnabled?: boolean;
  onRefresh?: () => void;
}

interface Payload<TQuery extends object> extends Pagination {
  enabled: boolean;
  queryParams: TQuery | undefined;
}

/**
 * 分页查询参数的助手
 *
 * 在 `usePagingQuery` 之上优化而来，接口更加友好，且新增支持排序的参数
 */
export function usePagingQueryHelper<TQuery extends object>(
  options?: QueryHelperHookOptions<TQuery>,
) {
  const { onRefresh } = options ?? {};

  const [payload, setPayload] = useState<Payload<TQuery>>(() => ({
    enabled: options?.defaultEnabled ?? true,
    page: options?.defaultPagination?.page ?? INIT_PAGE_NUMBER,
    size: options?.defaultPagination?.size ?? DEFAULT_PAGE_SIZE,
    queryParams: options?.defaultQueryParams,
    version: 0,
  }));

  const changePageNumber = useCallback(
    (pageNumber: number, pageSize?: number) => {
      setPayload((values) => ({
        ...values,
        size: pageSize ?? values.size,
        page: pageNumber,
      }));
    },
    [],
  );

  const changePageSize = useCallback(
    (pageSize: number, pageNumber?: number) => {
      setPayload((values) => ({
        ...values,
        size: pageSize,
        page: pageNumber ?? values.page,
      }));
    },
    [],
  );

  const queryMany = useCallback(
    (
      query?: TQuery | ((query: TQuery | undefined) => TQuery | undefined),
    ): void => {
      setPayload((values) => {
        const { queryParams: prevQueryParams } = values;

        // 深拷贝一份查询参数
        // WHY: 避免传入的参数被外部意外的修改掉，导致后续比较的结果不符合预期
        const newQuery = structuredClone(
          typeof query === 'function' ? query(prevQueryParams) : query,
        );

        const prevQueryParamsHash = hashKey([
          { ...prevQueryParams },
          { size: values.size, page: values.page },
        ]);
        const nextQueryParamsHash = hashKey([
          { ...newQuery },
          { size: values.size, page: INIT_PAGE_NUMBER },
        ]);

        if (prevQueryParamsHash === nextQueryParamsHash) {
          onRefresh?.();
          return {
            ...values,
            enabled: true,
          };
        }

        return {
          ...values,
          enabled: true,
          page: INIT_PAGE_NUMBER,
          queryParams: newQuery,
        };
      });
    },
    [onRefresh],
  );

  const pagination = useMemo<Pagination>(
    () => ({
      size: payload.size,
      page: payload.page,
    }),
    [payload.page, payload.size],
  );

  return {
    enabled: payload.enabled,
    changePageNumber,
    changePageSize,
    pagination,
    queryParams: payload.queryParams,
    queryMany,
  };
}
